/**
 * Feature catalog, pricing tiers, and requirements wizard data for HOA sales page.
 *
 * Source of truth for marketing — when adding features or add-ons, also update:
 * - HoaSalesPage.js, MarketingLandingPage.js, HoaBoardOnePager.js
 * - HelpPage.js, DeveloperGuidePage.js (marketing section)
 * - functions/nannyKnowledge.js, mobile/screens/HelpScreen.tsx
 */

/** Messaging pillars for AI-powered self-management vs traditional PMCs */
export const AI_MANAGEMENT_PILLARS = [
  {
    id: 'nanny',
    title: 'Nanny AI — your always-on community manager',
    description:
      'Answers owner questions about CC&Rs, billing, gate access, and portal navigation 24/7. Admins get voice-enabled help for day-to-day ops.',
    icon: '🤖',
  },
  {
    id: 'self-service',
    title: 'Owners self-serve — fewer board interruptions',
    description:
      'Pay dues, vote in polls, register plates, check mailbox deliveries, and find documents without emailing the board or calling a management line.',
    icon: '🏡',
  },
  {
    id: 'board-tools',
    title: 'Board operations in one admin hub',
    description:
      'Invoices, HOA finance (vendor AP), governance compliance, violations, emergency broadcasts, polls, user management, and monitoring — the work a PMC usually marks up and bundles.',
    icon: '📋',
  },
  {
    id: 'cost',
    title: 'Predictable cost — no per-door PMC markup',
    description:
      'Fixed platform pricing on your own infrastructure. Keep a local bookkeeper or attorney as needed; replace the full-service management contract.',
    icon: '💰',
  },
];

/** Compare typical management-company spend to platform pricing */
export const MANAGEMENT_COMPARISON = [
  {
    approach: 'Full-service management company',
    exampleLots: 100,
    exampleMonthly: 1200,
    monthlyLabel: '~$12/door',
    notes: 'Typical $8–$15 per lot/month; multi-year contracts, vendor markups, limited owner transparency',
  },
  {
    approach: 'Self-managed board + spreadsheets & email',
    exampleLots: 100,
    exampleMonthly: 0,
    monthlyLabel: '$0 software',
    notes: 'Volunteer burnout, inconsistent answers, no audit trail — owners still expect professional service',
  },
  {
    approach: 'This platform + Nanny AI (Professional)',
    exampleLots: 100,
    exampleMonthly: 399,
    monthlyLabel: '$399/mo flat',
    notes: 'AI-assisted self-management: portal, billing, gate, mailbox, admin tools — board stays in control',
    highlight: true,
  },
];

/** Board checklist for transitioning off a full-service management company */
export const PMC_REPLACEMENT_CHECKLIST = [
  {
    id: 'decide',
    phase: '1. Decide & align the board',
    summary: 'Confirm the community is ready to self-manage with software + AI instead of a PMC contract.',
    items: [
      { id: 'review-pmc-contract', label: 'Review current PMC contract — termination notice, fees, and data handoff clauses' },
      { id: 'cost-comparison', label: 'Compare PMC cost per lot vs. platform tier (use configurator on /for-communities)' },
      { id: 'board-vote', label: 'Board vote or owner meeting resolution to transition to self-management' },
      { id: 'retain-advisors', label: 'Confirm ongoing attorney and bookkeeper / CPA relationships' },
    ],
  },
  {
    id: 'plan',
    phase: '2. Plan the transition (90-day runway)',
    summary: 'Give owners lead time and avoid a service gap when the PMC contract ends.',
    items: [
      { id: 'transition-date', label: 'Set a target go-live date and PMC contract end date' },
      { id: 'owner-comms', label: 'Draft owner announcement — why, timeline, and how to get help (Nanny AI + portal)' },
      { id: 'data-export', label: 'Request full data export from PMC: owner roster, lot map, ledger, documents, violations' },
      { id: 'vendor-list', label: 'Inventory recurring vendors (landscape, gate, pool) and who will approve invoices' },
    ],
  },
  {
    id: 'setup',
    phase: '3. Configure your portal',
    summary: 'White-label deployment with billing, access control, and documents in one place.',
    items: [
      { id: 'branding-domain', label: 'Finalize community name, logo, colors, and custom domain' },
      { id: 'lot-roster', label: 'Import lots, owners, and admin accounts; verify custom claims' },
      { id: 'documents', label: 'Upload CC&Rs, bylaws, policies, and meeting minutes to HOA docs' },
      { id: 'stripe-billing', label: 'Connect Stripe and configure assessment / invoice templates' },
      { id: 'gate-mailbox', label: 'Integrate gate controller and Ring mailbox (if applicable)' },
      { id: 'nanny-knowledge', label: 'Train Nanny AI on community-specific rules and FAQs' },
    ],
  },
  {
    id: 'onboard',
    phase: '4. Onboard owners',
    summary: 'Replace the PMC phone tree with self-service and 24/7 AI answers.',
    items: [
      { id: 'welcome-email', label: 'Send login instructions and mobile app install links' },
      { id: 'demo-walkthrough', label: 'Host a short webinar or publish a 5-minute portal tour video' },
      { id: 'nanny-intro', label: 'Introduce Nanny AI as the first place to ask CC&R and billing questions' },
      { id: 'support-path', label: 'Publish escalation path: Nanny → portal help → board contact' },
    ],
  },
  {
    id: 'golive',
    phase: '5. Go live & wind down the PMC',
    summary: 'Run parallel for 2–4 weeks if possible, then close the management contract.',
    items: [
      { id: 'parallel-run', label: 'Optional parallel run: PMC + portal for one billing cycle' },
      { id: 'first-assessment', label: 'Issue first assessment through portal billing' },
      { id: 'terminate-pmc', label: 'Send PMC termination per contract; confirm final accounting handoff' },
      { id: 'update-signage', label: 'Update gate signage, website, and emergency contacts' },
    ],
  },
  {
    id: 'operate',
    phase: '6. Operate with AI-assisted self-management',
    summary: 'Volunteer board stays in control; software handles routine owner service.',
    items: [
      { id: 'board-roles', label: 'Document board officer roles (president, treasurer, violations, gate)' },
      { id: 'monthly-rhythm', label: 'Monthly rhythm: invoices, polls, emergencies, monitoring dashboard review' },
      { id: 'annual-review', label: 'Annual review of platform tier, add-ons, and Nanny knowledge updates' },
      { id: 'owner-feedback', label: 'Collect owner feedback after 90 days; adjust portal and AI answers' },
    ],
  },
];

/** Short bullets for the printable board one-pager */
export const BOARD_ONE_PAGER = {
  headline: 'AI HOA Management — Board One-Pager',
  subhead: 'Self-manage your community without a full-service management company contract.',
  problem: 'Typical PMCs charge $8–$15 per lot per month for owner calls, document storage, and basic billing — often with multi-year contracts and limited transparency.',
  solution: 'A white-labeled owner portal plus Nanny AI gives owners 24/7 answers and self-service while the board keeps direct control. Keep your attorney and bookkeeper; replace the PMC layer for day-to-day operations.',
  keepList: [
    'Community attorney for governing documents and collections',
    'Bookkeeper or CPA for annual audit and tax filings',
    'Local vendors you already trust',
    'Board governance and vendor approval authority',
  ],
  replaceList: [
    'Per-door PMC management fees',
    'Owner question phone tree and email backlog',
    'Scattered document links and PDF attachments',
    'Opaque billing for software the PMC resells',
  ],
  tiersSnapshot: [
    { name: 'Starter', monthly: 199, bestFor: 'Smaller self-managed communities' },
    { name: 'Professional', monthly: 399, bestFor: 'Billing, gate, mailbox — PMC replacement sweet spot' },
    { name: 'Enterprise', monthly: 649, bestFor: 'Nanny AI, environmental dashboards, ops monitoring' },
  ],
  optionalAddons: [
    {
      name: 'iOS & Android owner apps',
      estimate: '+$5,500 setup · +$135/mo',
      note: 'Not included in base tiers. Branded native apps, App Store / Play distribution, push notifications, and ongoing release maintenance — significant extra engineering vs. the responsive web portal.',
    },
    {
      name: 'Smart-home & amenity integrations',
      estimate: 'From +$1,200 setup · +$45/mo per system',
      note: 'Custom work — pool/clubhouse locks, HVAC, lighting, sensors. We connect what you already own (Shelly, Ring, Nest, Home Assistant, etc.) into the admin portal and owner app.',
    },
  ],
};

/** Custom smart-home and common-area integrations boards often need beyond the base portal */
export const SMART_HOME_INTEGRATIONS = {
  headline: 'Custom smart-home & amenity integrations',
  lead:
    'Many HOAs already have smart locks on a clubhouse, pool equipment on timers, or HVAC in shared buildings — but no single place for the board to monitor or control them. We wire your existing hardware into the portal so admins and (where appropriate) owners get one dashboard instead of five vendor apps.',
  pricingNote:
    'Priced per integration after a short site survey. Typical range: +$1,200–$4,500 setup and +$45–$95/mo per connected system, depending on device count, APIs, and whether owners need self-service access.',
  categories: [
    {
      id: 'access',
      title: 'Door locks & amenity access',
      icon: '🔐',
      summary: 'Clubhouse, pool gate, fitness room, and storage — scheduled codes, owner booking unlocks, and audit logs.',
      examples: ['Yale / Schlage / Kwikset smart locks', 'Keypad & Z-Wave deadbolts', 'Shelly / relay strike plates', 'Temporary guest codes tied to amenity reservations'],
      addonId: 'amenity-smart-locks',
    },
    {
      id: 'pool',
      title: 'Pool, spa & splash pad',
      icon: '🏊',
      summary: 'Pump schedules, heater status, cover sensors, and chemistry alerts surfaced to the board and maintenance.',
      examples: ['Jandy / Pentair / Hayward controllers', 'Freeze-risk alerts', 'Season open/close checklists', 'Occupancy or gate interlocks'],
      addonId: 'pool-spa-controls',
    },
    {
      id: 'hvac',
      title: 'HVAC & climate (common areas)',
      icon: '🌡️',
      summary: 'Clubhouse, mailroom, and utility buildings — remote setpoints, filter reminders, and after-hours setbacks.',
      examples: ['Nest / Ecobee thermostats', 'Mini-split Wi-Fi adapters', 'Humidity & freeze monitoring', 'Energy use snapshots for the board'],
      addonId: 'hvac-common-area',
    },
    {
      id: 'lighting',
      title: 'Lighting & site power',
      icon: '💡',
      summary: 'Path lights, parking areas, monument signs, and clubhouse scenes — schedules plus manual override from the admin portal.',
      examples: ['Shelly switches & dimmers', 'Lutron Caséta (via bridge)', 'Photocell status', 'Holiday / event lighting scenes'],
      addonId: 'smart-lighting',
    },
    {
      id: 'security',
      title: 'Cameras, gates & sensors',
      icon: '📡',
      summary: 'Extend beyond Ring mailbox — gate relays, door contacts, leak/freeze sensors, and well or pump monitors.',
      examples: ['Ring & other camera webhooks', 'Shelly gate relays (already supported)', 'Water leak & freeze sensors', 'Custom MQTT / Home Assistant feeds'],
      addonId: 'custom-sensors',
    },
    {
      id: 'custom',
      title: 'Anything else on your site',
      icon: '⚙️',
      summary: 'Irrigation controllers, elevator phones, marina lifts, workshop exhaust fans — if it has an API or a relay, we can usually integrate it.',
      examples: ['Rachio / RainMachine irrigation', 'Garage & boat-lift controllers', 'Generator exercise schedules', 'Vendor-maintained equipment read-only dashboards'],
      addonId: 'smart-home-custom',
    },
  ],
  platforms: [
    'Home Assistant',
    'Shelly',
    'Ring',
    'Google Nest',
    'Ecobee',
    'SmartThings',
    'Z-Wave / Zigbee hubs',
    'REST & MQTT devices',
  ],
};

export const FEATURE_CATEGORIES = [
  {
    id: 'ai-management',
    title: 'AI HOA management',
    description:
      'Run day-to-day community operations without hiring a full-service management company. Nanny AI plus board tools cover what owners and volunteers used to outsource.',
    features: [
      {
        id: 'nanny',
        name: 'Nanny AI assistant',
        description: '24/7 answers on rules, billing, navigation, gate help, and local community context.',
        status: 'included',
      },
      {
        id: 'owner-portal',
        name: 'Owner self-service portal',
        description: 'Reduces “call the management company” tickets — payments, polls, documents, and status in one place.',
        status: 'included',
      },
      {
        id: 'admin-ops',
        name: 'Board & admin operations',
        description: 'User management, invoices, violations, emergencies, and ops monitoring without PMC overhead.',
        status: 'included',
      },
      {
        id: 'transparent-pricing',
        name: 'Transparent platform pricing',
        description: 'Fixed monthly fee on dedicated infrastructure — not a percentage of assessments or per-door PMC markup.',
        status: 'included',
      },
    ],
  },
  {
    id: 'core',
    title: 'Core owner portal',
    description: 'Essential communication and self-service every HOA expects.',
    features: [
      { id: 'dashboard', name: 'Owner dashboard', description: 'Weather, local news, events, and mail status on one home screen.', status: 'included' },
      { id: 'documents', name: 'HOA documents', description: 'CC&Rs, bylaws, policies, and shared file library.', status: 'included' },
      { id: 'calendar', name: 'Community calendar', description: 'Meetings, social events, and maintenance windows.', status: 'included' },
      { id: 'polls', name: 'Polls & voting', description: 'Board surveys and community votes with admin controls.', status: 'included' },
      { id: 'message-board', name: 'Message board', description: 'Threaded discussions with moderation tools.', status: 'included' },
      { id: 'owners', name: 'Owner directory', description: 'Lot-linked contacts with privacy preferences.', status: 'included' },
      { id: 'board', name: 'Board & committees', description: 'Board roster, meeting minutes, and contractor list.', status: 'included' },
      { id: 'help', name: 'Help center', description: 'Role-aware guides and searchable FAQs.', status: 'included' },
      { id: 'preferences', name: 'User preferences', description: 'Notification settings and profile management.', status: 'included' },
      { id: 'legal', name: 'Privacy & terms', description: 'Community-specific legal pages.', status: 'included' },
    ],
  },
  {
    id: 'operations',
    title: 'Operations & billing',
    description: 'Run day-to-day HOA business with payments and access control.',
    features: [
      { id: 'invoices', name: 'HOA billing (Stripe)', description: 'Assessments, saved cards, and payment history per lot.', status: 'included' },
      { id: 'hoa-finance', name: 'HOA finance (AP)', description: 'Operating bank balances, vendor bills linked to contractors, approval workflow, and payment recording.', status: 'included' },
      { id: 'hoa-compliance', name: 'Governance & compliance', description: 'FY legal checklist, meeting notice tracking, elections workflow, and owner governance calendar.', status: 'included' },
      { id: 'gate', name: 'Gate access', description: 'License plate registration, remote open, and audit logs.', status: 'included' },
      { id: 'mailbox', name: 'Mailbox intelligence', description: 'Ring-powered delivery detection with daily history.', status: 'included' },
      { id: 'vehicle-log', name: 'Vehicle sighting log', description: 'Admin review of gate camera sightings.', status: 'included' },
      { id: 'emergency', name: 'Emergency broadcasts', description: 'Urgent email blasts to all owners.', status: 'included' },
      {
        id: 'weather-alerts',
        name: 'NWS weather alerts',
        description: 'Dismissible watches/warnings banner, radar link, and message-board posts for new bulletins.',
        status: 'included',
      },
      { id: 'rentals', name: 'Rental registry', description: 'Short-term rental tracking and owner compliance.', status: 'included' },
      { id: 'violations', name: 'Violation tracking', description: 'Issue notices, track resolution, and export compliance reports.', status: 'beta' },
      { id: 'arc', name: 'Architectural review (ARC)', description: 'Owners submit exterior change requests with photos; board approves or denies.', status: 'beta' },
    ],
  },
  {
    id: 'engagement',
    title: 'Community engagement',
    description: 'Media, maps, and tools that keep owners connected.',
    features: [
      { id: 'community-pics', name: 'Community photos', description: 'Shared galleries with comments and moderation.', status: 'included' },
      { id: 'video', name: 'Onsite webcam', description: 'Live or recorded community camera feeds.', status: 'included' },
      { id: 'neighborhood-map', name: 'Neighborhood map', description: 'Interactive lot map with owner overlays.', status: 'included' },
      { id: 'shared-files', name: 'Shared files', description: 'Member uploads with admin approval.', status: 'included' },
    ],
  },
  {
    id: 'mountain',
    title: 'Mountain & environmental',
    description: 'Live dashboards built for rural and mountain communities (NMR specialty).',
    features: [
      { id: 'well', name: 'Well monitoring', description: 'Water level charts and alert thresholds.', status: 'included' },
      { id: 'seismic', name: 'Seismic activity', description: 'USGS earthquake feed for your region.', status: 'included' },
      { id: 'river', name: 'Local river gauges', description: 'Stream flow and flood-stage awareness.', status: 'included' },
      { id: 'roads', name: 'Road conditions', description: 'Seasonal road status and photo updates.', status: 'included' },
      { id: 'flight-radar', name: 'Flight radar', description: 'Local air traffic map for noise awareness.', status: 'included' },
      { id: 'star-map', name: 'Star map', description: 'Night-sky chart tuned to your coordinates.', status: 'included' },
    ],
  },
  {
    id: 'discovery',
    title: 'Identification & discovery',
    description: 'Educational AI galleries for local flora, fauna, and geology.',
    features: [
      { id: 'plants', name: 'Plant & tree ID', description: 'Community plant gallery with AI-assisted lookup.', status: 'included' },
      { id: 'animals', name: 'Wildlife ID', description: 'Local species reference and sightings.', status: 'included' },
      { id: 'minerals', name: 'Rock & mineral ID', description: 'Regional geology gallery.', status: 'included' },
    ],
  },
  {
    id: 'smart-integrations',
    title: 'Smart-home & amenity integrations',
    description:
      'Optional custom work — connect clubhouse locks, pool equipment, HVAC, lighting, and site sensors into the same portal your board already uses.',
    features: [
      { id: 'amenity-locks', name: 'Amenity door locks', description: 'Clubhouse, pool, and fitness access — codes, booking unlocks, audit trail.', status: 'addon' },
      { id: 'pool-controls', name: 'Pool & spa controls', description: 'Pump schedules, heater status, freeze alerts, and maintenance panels.', status: 'addon' },
      { id: 'hvac-common', name: 'Common-area HVAC', description: 'Thermostats and climate monitoring for shared buildings.', status: 'addon' },
      { id: 'smart-lighting', name: 'Site lighting', description: 'Scheduled path, parking, and monument lighting with admin override.', status: 'addon' },
      { id: 'smart-sensors', name: 'Sensors & relays', description: 'Leak, freeze, gate, well, and custom IoT feeds — building on gate & environmental modules.', status: 'addon' },
    ],
  },
  {
    id: 'platform',
    title: 'Platform & admin',
    description: 'Backend, AI assistant, operations monitoring, and optional native mobile apps.',
    features: [
      { id: 'admin-portal', name: 'Admin portal', description: 'Unified hub for users, billing, polls, and mailbox setup.', status: 'included' },
      { id: 'monitoring', name: 'Ops monitoring', description: 'Firebase health, scheduled jobs, watcher, and API catalog.', status: 'included' },
      { id: 'nanny', name: 'Nanny AI assistant', description: 'AI community manager — owner Q&A, portal navigation, admin gate/ops help, 24/7.', status: 'included' },
      { id: 'mobile-apps', name: 'iOS & Android apps', description: 'Optional add-on — branded native apps with store distribution, push alerts, and release maintenance.', status: 'addon' },
      { id: 'white-label', name: 'White-label branding', description: 'Your name, domain, colors, and legal entity.', status: 'included' },
      { id: 'firebase', name: 'Firebase backend', description: 'Firestore, Auth, Functions, Hosting, and security rules.', status: 'included' },
    ],
  },
];

export const ADDON_MODULES = [
  {
    id: 'mobile-apps',
    name: 'iOS & Android owner apps',
    description:
      'Branded Expo/React Native apps for App Store and Google Play — separate builds, store accounts, push notifications, install links, and ongoing release work beyond the responsive web portal.',
    estimate: '+$5,500 setup · +$135/mo',
    tags: ['engagement', 'platform'],
    status: 'available',
  },
  {
    id: 'arc',
    name: 'Architectural review (ARC)',
    description: 'Submit, review, and approve exterior change requests with photo attachments.',
    estimate: 'Included in platform (beta)',
    tags: ['compliance'],
    status: 'available',
  },
  {
    id: 'violations',
    name: 'Violation tracking',
    description: 'Issue notices, track resolution, and export compliance reports.',
    estimate: '+$900 setup · +$35/mo',
    tags: ['compliance'],
    status: 'available',
  },
  {
    id: 'work-orders',
    name: 'Maintenance & work orders',
    description: 'Owner requests, vendor assignment, and status tracking.',
    estimate: '+$1,500 setup · +$55/mo',
    tags: ['operations'],
    status: 'available',
  },
  {
    id: 'amenity-booking',
    name: 'Amenity reservations',
    description: 'Pool, clubhouse, and tennis court booking with calendars.',
    estimate: '+$800 setup · +$30/mo',
    tags: ['engagement'],
    status: 'available',
  },
  {
    id: 'visitor-prereg',
    name: 'Visitor pre-registration',
    description: 'Owners register guests; gate receives plate list automatically.',
    estimate: '+$1,000 setup · +$40/mo',
    tags: ['gated', 'operations'],
    status: 'available',
  },
  {
    id: 'sms-alerts',
    name: 'SMS text alerts',
    description: 'Twilio-powered urgent texts alongside email notifications.',
    estimate: '+$600 setup · +$25/mo + usage',
    tags: ['communications'],
    status: 'available',
  },
  {
    id: 'esign',
    name: 'Document e-signatures',
    description: 'Collect signed acknowledgments for rule changes and forms.',
    estimate: '+$700 setup · +$20/mo',
    tags: ['compliance'],
    status: 'available',
  },
  {
    id: 'bank-linking',
    name: 'Bank account linking (Plaid)',
    description: 'Auto-import HOA operating account balances and transactions; pairs with HOA Finance AP.',
    estimate: '+$1,200 setup · +$45/mo',
    tags: ['billing', 'finance'],
    status: 'roadmap',
  },
  {
    id: 'accounting-sync',
    name: 'Accounting integration',
    description: 'QuickBooks or Xero sync for owner AR (Stripe) and vendor AP from HOA Finance.',
    estimate: '+$2,000 setup · +$65/mo',
    tags: ['billing', 'finance'],
    status: 'roadmap',
  },
  {
    id: 'multi-community',
    name: 'Management company dashboard',
    description: 'One login across multiple HOAs with per-community branding.',
    estimate: '+$3,500 setup · +$120/mo per community',
    tags: ['enterprise'],
    status: 'roadmap',
  },
  {
    id: 'cctv',
    name: 'Security camera portal',
    description: 'Owner-facing or admin-only views from existing NVR feeds.',
    estimate: '+$1,800 setup · +$50/mo',
    tags: ['gated', 'security'],
    status: 'roadmap',
  },
  {
    id: 'wildfire-aqi',
    name: 'Wildfire & air quality',
    description: 'PurpleAir / AirNow widgets with custom alert thresholds.',
    estimate: '+$500 setup · +$20/mo',
    tags: ['mountain', 'environmental'],
    status: 'available',
  },
  {
    id: 'classifieds',
    name: 'Community marketplace',
    description: 'Buy/sell/trade board with admin moderation.',
    estimate: '+$600 setup · +$25/mo',
    tags: ['engagement'],
    status: 'available',
  },
  {
    id: 'parking',
    name: 'Parking permits',
    description: 'Guest and annual permit issuance with plate linkage.',
    estimate: '+$900 setup · +$35/mo',
    tags: ['gated'],
    status: 'available',
  },
  {
    id: 'amenity-smart-locks',
    name: 'Amenity smart locks',
    description:
      'Clubhouse, pool, fitness, and storage doors — owner booking unlocks, rotating codes, and full access audit in the admin portal.',
    estimate: '+$1,800 setup · +$55/mo',
    tags: ['amenities', 'smart-home', 'engagement'],
    status: 'roadmap',
  },
  {
    id: 'pool-spa-controls',
    name: 'Pool & spa integration',
    description:
      'Pump/heater schedules, freeze-risk alerts, chemistry reminders, and season open/close status for board and maintenance.',
    estimate: '+$2,200 setup · +$65/mo',
    tags: ['amenities', 'smart-home'],
    status: 'roadmap',
  },
  {
    id: 'hvac-common-area',
    name: 'Common-area HVAC',
    description:
      'Nest, Ecobee, or mini-split adapters for clubhouse and utility buildings — setbacks, alerts, and filter reminders.',
    estimate: '+$1,200 setup · +$45/mo',
    tags: ['amenities', 'smart-home', 'operations'],
    status: 'roadmap',
  },
  {
    id: 'smart-lighting',
    name: 'Site & amenity lighting',
    description:
      'Shelly, Lutron, or relay-controlled path, parking, and monument lighting — schedules plus one-tap override for events.',
    estimate: '+$1,400 setup · +$50/mo',
    tags: ['amenities', 'smart-home'],
    status: 'roadmap',
  },
  {
    id: 'custom-sensors',
    name: 'Custom sensor & relay feeds',
    description: 'Leak, freeze, gate, well, weather, and MQTT/Home Assistant devices — extends gate and environmental monitoring.',
    estimate: '+$1,500 setup · +$55/mo',
    tags: ['mountain', 'environmental', 'smart-home', 'gated'],
    status: 'roadmap',
  },
  {
    id: 'smart-home-custom',
    name: 'Custom smart-home integration',
    description:
      'Site survey + scoped integration for irrigation, lifts, generators, or other vendor hardware not covered above.',
    estimate: 'Custom quote (from +$2,500 setup)',
    tags: ['amenities', 'smart-home', 'enterprise'],
    status: 'roadmap',
  },
];

export const PRICING_TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    monthly: 199,
    setup: 2500,
    lotCap: 75,
    perLotOver: 2.5,
    tagline: 'Self-managed communities getting off email & spreadsheets',
    includes: ['dashboard', 'documents', 'calendar', 'polls', 'message-board', 'owners', 'board', 'help', 'legal', 'neighborhood-map'],
    highlights: [
      'Up to 75 lots included',
      'White-label domain & branding',
      'Admin user management',
      'Email support',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    monthly: 399,
    setup: 4500,
    lotCap: 150,
    perLotOver: 2,
    tagline: 'Replace PMC busywork — billing, gate, mailbox & admin hub',
    popular: true,
    includes: ['starter', 'invoices', 'hoa-finance', 'hoa-compliance', 'gate', 'mailbox', 'vehicle-log', 'emergency', 'community-pics', 'preferences', 'admin-portal'],
    highlights: [
      'Everything in Starter',
      'Stripe billing & invoices',
      'HOA finance — vendor AP & bank balances',
      'Governance compliance — meetings & elections',
      'Gate access & vehicle log',
      'Ring mailbox integration',
      'Admin portal',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: 649,
    setup: 6500,
    lotCap: 300,
    perLotOver: 1.5,
    tagline: 'AI HOA management — Nanny, environmental ops & monitoring',
    includes: ['professional', 'video', 'rentals', 'well', 'seismic', 'river', 'roads', 'flight-radar', 'star-map', 'plants', 'animals', 'minerals', 'nanny', 'monitoring'],
    highlights: [
      'Everything in Professional',
      'Environmental dashboards',
      'Nanny AI community manager',
      'Ops monitoring & priority support',
      'Mobile apps available as add-on (see below)',
    ],
  },
];

export const MARKET_COMPARISON = [
  { vendor: 'Full-service management company', model: 'Per door', monthly: 1200, notes: '~$12/door × 100 lots; contracts, markups, limited transparency' },
  { vendor: 'Raklet Premium', model: 'SaaS', monthly: 399, notes: 'Up to 10k contacts; generic HOA templates; no AI ops layer' },
  { vendor: 'Raklet custom mobile apps', model: 'Add-on', monthly: 299, notes: 'Branded iOS/Android on Raklet infrastructure' },
  { vendor: 'HOA Express Engage', model: 'SaaS', monthly: 21, notes: 'Starts at 0–25 households; scales with lot count' },
  { vendor: 'This platform (Professional)', model: 'Dedicated + AI', monthly: 399, notes: 'Self-management stack: billing, gate, mailbox, your Firebase project — web portal included' },
  { vendor: 'This platform — mobile apps', model: 'Add-on', monthly: 135, notes: '+$5,500 setup · branded iOS/Android, store distribution, push — not bundled in base tiers' },
];

export const WIZARD_QUESTIONS = [
  {
    id: 'communityName',
    type: 'text',
    label: 'Community name',
    placeholder: 'e.g. Cedar Ridge HOA',
    required: true,
  },
  {
    id: 'contactEmail',
    type: 'email',
    label: 'Your email',
    placeholder: 'board@yourhoa.org',
    required: true,
  },
  {
    id: 'lotCount',
    type: 'select',
    label: 'Number of lots / households',
    options: [
      { value: 'under50', label: 'Under 50', lots: 40 },
      { value: '50-100', label: '50 – 100', lots: 75 },
      { value: '100-200', label: '100 – 200', lots: 150 },
      { value: '200-400', label: '200 – 400', lots: 300 },
      { value: '400plus', label: '400+', lots: 500 },
    ],
    required: true,
  },
  {
    id: 'communityType',
    type: 'select',
    label: 'Community type',
    options: [
      { value: 'mountain', label: 'Mountain / rural', tags: ['mountain', 'environmental'] },
      { value: 'gated', label: 'Gated suburban', tags: ['gated', 'security'] },
      { value: 'residential', label: 'Standard residential', tags: [] },
      { value: 'resort', label: 'Resort / vacation community', tags: ['engagement', 'rentals'] },
      { value: 'mixed', label: 'Mixed / other', tags: [] },
    ],
    required: true,
  },
  {
    id: 'managementModel',
    type: 'select',
    label: 'How does your HOA operate today?',
    options: [
      { value: 'pmc', label: 'We use a management company', tierBoost: 'professional' },
      { value: 'replacing-pmc', label: 'Replacing our management company', tierBoost: 'enterprise' },
      { value: 'self', label: 'Self-managed volunteer board', tierBoost: 'starter' },
      { value: 'hybrid', label: 'Hybrid — bookkeeper or part-time manager + volunteers', tierBoost: 'professional' },
    ],
    required: true,
  },
  {
    id: 'priorities',
    type: 'multiselect',
    label: 'Top priorities (pick up to 5)',
    max: 5,
    options: [
      { value: 'billing', label: 'Online dues & payments', modules: ['invoices'], tierBoost: 'professional' },
      { value: 'gate', label: 'Gate & access control', modules: ['gate', 'vehicle-log'], tierBoost: 'professional', tags: ['gated'] },
      { value: 'mailbox', label: 'Mail / package awareness', modules: ['mailbox'], tierBoost: 'professional' },
      { value: 'communications', label: 'Polls, board updates, emergencies', modules: ['polls', 'message-board', 'emergency'], tierBoost: 'starter' },
      { value: 'documents', label: 'Document library & minutes', modules: ['documents', 'board'], tierBoost: 'starter' },
      { value: 'environmental', label: 'Environmental monitoring', modules: ['well', 'river', 'roads', 'seismic'], tierBoost: 'enterprise', tags: ['mountain'] },
      { value: 'mobile', label: 'Mobile apps for owners', modules: ['mobile-apps'], addonIds: ['mobile-apps'] },
      { value: 'ai-assistant', label: 'AI HOA management (Nanny)', modules: ['nanny'], tierBoost: 'enterprise' },
      { value: 'replace-pmc', label: 'Replace management company overhead', modules: ['nanny', 'invoices', 'admin-portal'], tierBoost: 'enterprise' },
      { value: 'compliance', label: 'ARC / violations / compliance', addonIds: ['arc', 'violations'], tags: ['compliance'] },
      { value: 'amenities', label: 'Clubhouse / pool / amenities', addonIds: ['amenity-booking', 'amenity-smart-locks'], tags: ['amenities'] },
      { value: 'smart-home', label: 'Smart-home & site controls', addonIds: ['smart-home-custom'], tags: ['smart-home', 'amenities'] },
    ],
  },
  {
    id: 'integrations',
    type: 'multiselect',
    label: 'Integrations you need',
    options: [
      { value: 'stripe', label: 'Stripe payments', modules: ['invoices'] },
      { value: 'ring', label: 'Ring doorbells / cameras', modules: ['mailbox'] },
      { value: 'gate-controller', label: 'Existing gate controller', modules: ['gate'] },
      { value: 'quickbooks', label: 'QuickBooks / accounting', addonIds: ['accounting-sync'] },
      { value: 'twilio', label: 'SMS (Twilio)', addonIds: ['sms-alerts'] },
      { value: 'smart-locks', label: 'Clubhouse / pool door locks', addonIds: ['amenity-smart-locks'] },
      { value: 'pool-spa', label: 'Pool / spa equipment', addonIds: ['pool-spa-controls'] },
      { value: 'hvac', label: 'Common-area HVAC', addonIds: ['hvac-common-area'] },
      { value: 'smart-lighting', label: 'Site & amenity lighting', addonIds: ['smart-lighting'] },
      { value: 'home-assistant', label: 'Home Assistant / MQTT hub', addonIds: ['custom-sensors', 'smart-home-custom'] },
      { value: 'none', label: 'None yet / not sure', modules: [] },
    ],
  },
  {
    id: 'timeline',
    type: 'select',
    label: 'Desired launch timeline',
    options: [
      { value: 'asap', label: 'ASAP (4–6 weeks)' },
      { value: 'quarter', label: 'This quarter (2–3 months)' },
      { value: 'flexible', label: 'Flexible' },
    ],
  },
  {
    id: 'branding',
    type: 'select',
    label: 'Branding status',
    options: [
      { value: 'ready', label: 'Logo, colors, and domain ready' },
      { value: 'partial', label: 'Some assets; need help' },
      { value: 'new', label: 'Starting from scratch' },
    ],
  },
  {
    id: 'notes',
    type: 'textarea',
    label: 'Anything else we should know',
    placeholder: 'PMC contract end date, clubhouse/pool smart locks, HVAC brands, Home Assistant, data migration…',
  },
];

/** Flat list of all included feature IDs for quick lookup */
export function getAllIncludedFeatureIds() {
  const ids = new Set();
  FEATURE_CATEGORIES.forEach((cat) => {
    cat.features.forEach((f) => {
      if (f.status === 'included') ids.add(f.id);
    });
  });
  return ids;
}

/** Recommend tier from wizard answers */
export function recommendTier(answers) {
  const lotOption = WIZARD_QUESTIONS.find((q) => q.id === 'lotCount')?.options.find((o) => o.value === answers.lotCount);
  const lots = lotOption?.lots || 75;

  let tierId = 'starter';
  const priorities = answers.priorities || [];

  const priorityDefs = WIZARD_QUESTIONS.find((q) => q.id === 'priorities')?.options || [];
  priorities.forEach((p) => {
    const def = priorityDefs.find((o) => o.value === p);
    if (def?.tierBoost === 'enterprise') tierId = 'enterprise';
    else if (def?.tierBoost === 'professional' && tierId !== 'enterprise') tierId = 'professional';
  });

  const integrations = answers.integrations || [];
  if (integrations.some((i) => ['stripe', 'ring', 'gate-controller'].includes(i))) {
    if (tierId === 'starter') tierId = 'professional';
  }
  if (answers.communityType === 'mountain') {
    tierId = 'enterprise';
  }
  if (['replacing-pmc', 'pmc'].includes(answers.managementModel) && tierId === 'starter') {
    tierId = 'professional';
  }
  if (answers.managementModel === 'replacing-pmc') {
    tierId = 'enterprise';
  }
  if ((answers.priorities || []).includes('replace-pmc')) {
    tierId = 'enterprise';
  }

  let tier = PRICING_TIERS.find((t) => t.id === tierId) || PRICING_TIERS[0];

  if (lots > tier.lotCap) {
    const order = ['starter', 'professional', 'enterprise'];
    const idx = order.indexOf(tier.id);
    if (idx < order.length - 1 && lots > PRICING_TIERS[idx].lotCap) {
      tier = PRICING_TIERS[Math.min(idx + 1, order.length - 1)];
    }
    if (lots > tier.lotCap && tier.id !== 'enterprise') {
      tier = PRICING_TIERS[2];
    }
  }

  return { tier, lots };
}

/** Collect suggested add-ons from answers */
export function suggestAddons(answers) {
  const suggested = new Set();
  const tags = new Set();

  const typeOption = WIZARD_QUESTIONS.find((q) => q.id === 'communityType')?.options.find((o) => o.value === answers.communityType);
  (typeOption?.tags || []).forEach((t) => tags.add(t));

  const priorityDefs = WIZARD_QUESTIONS.find((q) => q.id === 'priorities')?.options || [];
  (answers.priorities || []).forEach((p) => {
    const def = priorityDefs.find((o) => o.value === p);
    (def?.addonIds || []).forEach((id) => suggested.add(id));
    (def?.tags || []).forEach((t) => tags.add(t));
  });

  const integrationDefs = WIZARD_QUESTIONS.find((q) => q.id === 'integrations')?.options || [];
  (answers.integrations || []).forEach((i) => {
    const def = integrationDefs.find((o) => o.value === i);
    (def?.addonIds || []).forEach((id) => suggested.add(id));
  });

  if (tags.has('gated')) {
    suggested.add('visitor-prereg');
  }
  if (tags.has('compliance')) {
    suggested.add('arc');
    suggested.add('violations');
  }
  if (tags.has('mountain')) {
    suggested.add('wildfire-aqi');
  }
  if ((answers.priorities || []).includes('mobile')) {
    suggested.add('mobile-apps');
  }
  if (tags.has('amenities')) {
    suggested.add('amenity-booking');
    suggested.add('amenity-smart-locks');
  }
  if (tags.has('smart-home')) {
    suggested.add('smart-home-custom');
  }
  if (answers.communityType === 'resort') {
    suggested.add('pool-spa-controls');
    suggested.add('amenity-smart-locks');
  }

  return ADDON_MODULES.filter((a) => suggested.has(a.id));
}

/** Estimate monthly + setup from tier, lots, and add-ons */
export function estimatePricing(tier, lots, addons = []) {
  const lotOverage = Math.max(0, lots - tier.lotCap) * tier.perLotOver;
  const addonSetup = addons.reduce((sum, a) => {
    const match = a.estimate.match(/\$([0-9,]+)\s*setup/);
    return sum + (match ? parseInt(match[1].replace(/,/g, ''), 10) : 0);
  }, 0);
  const addonMonthly = addons.reduce((sum, a) => {
    const match = a.estimate.match(/\+\$([0-9,]+)\/mo/);
    return sum + (match ? parseInt(match[1].replace(/,/g, ''), 10) : 0);
  }, 0);

  return {
    monthly: tier.monthly + lotOverage + addonMonthly,
    setup: tier.setup + addonSetup,
    lotOverage,
    addonMonthly,
  };
}

/** Build requirements brief text for copy / email */
export function buildRequirementsBrief(answers, tier, lots, addons, pricing) {
  const lines = [];
  lines.push('HOA Community Portal — Requirements Brief');
  lines.push('========================================');
  lines.push('');
  if (answers.communityName) lines.push(`Community: ${answers.communityName}`);
  if (answers.contactEmail) lines.push(`Contact: ${answers.contactEmail}`);
  lines.push(`Lots / households: ~${lots}`);
  if (answers.communityType) lines.push(`Community type: ${answers.communityType}`);
  if (answers.managementModel) {
    const mgmtDef = WIZARD_QUESTIONS.find((q) => q.id === 'managementModel')?.options.find((o) => o.value === answers.managementModel);
    lines.push(`Management model: ${mgmtDef?.label || answers.managementModel}`);
  }
  if (answers.timeline) lines.push(`Timeline: ${answers.timeline}`);
  if (answers.branding) lines.push(`Branding: ${answers.branding}`);
  lines.push('');
  lines.push(`Recommended tier: ${tier.name} ($${tier.monthly}/mo base)`);
  lines.push(`Estimated monthly: $${pricing.monthly}/mo`);
  lines.push(`Estimated setup: $${pricing.setup.toLocaleString()} one-time`);
  lines.push('');
  if (answers.priorities?.length) {
    lines.push('Priorities:');
    const priorityDefs = WIZARD_QUESTIONS.find((q) => q.id === 'priorities')?.options || [];
    answers.priorities.forEach((p) => {
      const def = priorityDefs.find((o) => o.value === p);
      lines.push(`  • ${def?.label || p}`);
    });
    lines.push('');
  }
  if (answers.integrations?.length) {
    lines.push('Integrations:');
    const intDefs = WIZARD_QUESTIONS.find((q) => q.id === 'integrations')?.options || [];
    answers.integrations.forEach((i) => {
      const def = intDefs.find((o) => o.value === i);
      lines.push(`  • ${def?.label || i}`);
    });
    lines.push('');
  }
  if (addons.length) {
    lines.push('Suggested add-on modules:');
    addons.forEach((a) => lines.push(`  • ${a.name} — ${a.estimate}`));
    lines.push('');
  }
  if (answers.notes?.trim()) {
    lines.push('Additional notes:');
    lines.push(answers.notes.trim());
    lines.push('');
  }
  lines.push('---');
  lines.push('Generated from the HOA Community Portal configurator.');
  return lines.join('\n');
}